/**
 * Kanban-näkymä tilauksille
 * 
 * Näyttää tilaukset status-sarakkeina ja tukee drag & drop -siirtoja.
 */

import { useMemo, useCallback } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  type OrderStatus,
} from "@/config/constants";
import type { OrderListItem } from "@/types/orders";
import { OrderKanbanCard } from "./OrderKanbanCard";

/** Sallitut statuksen siirtymät drag & dropissa */
const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  draft: ["confirmed"],
  confirmed: ["draft", "ready_for_installation"],
  ready_for_installation: ["confirmed", "installed"],
  installed: ["ready_for_installation", "completed"],
  completed: ["installed"],
};

/** Kanban-sarakkeiden järjestys (cancelled piilotetaan) */
const KANBAN_COLUMNS: OrderStatus[] = [
  "draft",
  "confirmed",
  "ready_for_installation",
  "installed",
  "completed",
];

// Column colors for the header indicator
const COLUMN_HEADER_COLORS: Record<OrderStatus, string> = {
  draft: "bg-muted",
  confirmed: "bg-primary",
  ready_for_installation: "bg-secondary",
  installed: "bg-primary",
  completed: "bg-muted",
  cancelled: "bg-destructive",
};

interface KanbanColumnProps {
  status: OrderStatus;
  orders: OrderListItem[];
  isOver?: boolean;
}

function KanbanColumn({ status, orders }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex flex-col min-w-[280px] max-w-[320px] rounded-lg border border-border bg-card",
        isOver && "ring-2 ring-primary/50 bg-accent/30"
      )}
    >
      {/* Column Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border">
        <div className={cn("w-2 h-2 rounded-full", COLUMN_HEADER_COLORS[status])} />
        <span className="font-medium text-sm">
          {ORDER_STATUS_LABELS[status]}
        </span>
        <Badge variant="secondary" className="ml-auto text-xs">
          {orders.length}
        </Badge>
      </div>

      {/* Cards */}
      <ScrollArea className="flex-1 p-2" style={{ maxHeight: "calc(100vh - 320px)" }}>
        <SortableContext
          items={orders.map((o) => o.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {orders.map((order) => (
              <OrderKanbanCard key={order.id} order={order} />
            ))}
            {orders.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-8">
                Ei tilauksia
              </div>
            )}
          </div>
        </SortableContext>
      </ScrollArea>
    </div>
  );
}

interface OrdersKanbanProps {
  orders: OrderListItem[];
  isLoading: boolean;
  onStatusChange: (orderId: string, newStatus: string, oldStatus: string) => void;
}

export function OrdersKanban({ orders, isLoading, onStatusChange }: OrdersKanbanProps) {
  const [activeOrder, setActiveOrder] = useState<OrderListItem | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  // Group orders by status
  const columns = useMemo(() => {
    const grouped: Record<OrderStatus, OrderListItem[]> = {
      draft: [],
      confirmed: [],
      ready_for_installation: [],
      installed: [],
      completed: [],
      cancelled: [],
    };

    for (const order of orders) {
      const status = order.status as OrderStatus;
      if (grouped[status]) {
        grouped[status].push(order);
      }
    }

    return grouped;
  }, [orders]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const order = orders.find((o) => o.id === event.active.id);
    setActiveOrder(order || null);
  }, [orders]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveOrder(null);
      const { active, over } = event;
      if (!over) return;

      const orderId = active.id as string;
      const order = orders.find((o) => o.id === orderId);
      if (!order) return;

      // Determine target column
      let targetStatus = over.id as string;

      // If dropped on a card, find which column it belongs to
      if (!KANBAN_COLUMNS.includes(targetStatus as OrderStatus)) {
        const targetOrder = orders.find((o) => o.id === targetStatus);
        if (targetOrder) {
          targetStatus = targetOrder.status;
        } else {
          return;
        }
      }

      // Skip if same column
      if (order.status === targetStatus) return;

      // Check allowed transitions
      const allowed = ALLOWED_TRANSITIONS[order.status];
      if (!allowed || !allowed.includes(targetStatus)) return;

      onStatusChange(orderId, targetStatus, order.status);
    },
    [orders, onStatusChange]
  );

  if (isLoading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {KANBAN_COLUMNS.map((status) => (
          <div key={status} className="min-w-[280px] space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {KANBAN_COLUMNS.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            orders={columns[status]}
          />
        ))}
      </div>

      <DragOverlay>
        {activeOrder && (
          <div className="w-[300px] opacity-90">
            <OrderKanbanCard order={activeOrder} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
